
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * EndReadSnapshotResult struct
 */
class EndReadSnapshotResult {
  /**
   * Copy struct (shallow copy)
   * @this {!EndReadSnapshotResult}
   * @param {!EndReadSnapshotResult} other Other struct
   * @returns {!EndReadSnapshotResult} This struct
   */
  copy (other) {
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!EndReadSnapshotResult}
   * @returns {!EndReadSnapshotResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new EndReadSnapshotResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new EndReadSnapshotResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!EndReadSnapshotResult}
   * @param {!EndReadSnapshotResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof EndReadSnapshotResult)) {
      throw new TypeError('Instance of EndReadSnapshotResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!EndReadSnapshotResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return EndReadSnapshotResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!EndReadSnapshotResult} other Object value
   * @returns {!EndReadSnapshotResult} Created struct
   */
  static fromObject (other) {
    return new EndReadSnapshotResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!EndReadSnapshotResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return EndReadSnapshotResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!EndReadSnapshotResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4216
  }
}

export { EndReadSnapshotResult };

/**
 * Fast Binary Encoding EndReadSnapshotResult field model
 */
class FieldModelEndReadSnapshotResult extends fbe.FieldModel {
  /**
   * Get the field size
   * @this {!FieldModelEndReadSnapshotResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelEndReadSnapshotResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4
  }

  /**
   * Get the field extra size
   * @this {!FieldModelEndReadSnapshotResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelEndReadSnapshotResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelEndReadSnapshotResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelEndReadSnapshotResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4216
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelEndReadSnapshotResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelEndReadSnapshotResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelEndReadSnapshotResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelEndReadSnapshotResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelEndReadSnapshotResult}
   * @param {!EndReadSnapshotResult} fbeValue Default value, defaults is new EndReadSnapshotResult()
   * @returns {!EndReadSnapshotResult} EndReadSnapshotResult value
   */
  get (fbeValue = new EndReadSnapshotResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelEndReadSnapshotResult}
   * @param {!EndReadSnapshotResult} fbeValue EndReadSnapshotResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelEndReadSnapshotResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelEndReadSnapshotResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelEndReadSnapshotResult}
   * @param {!EndReadSnapshotResult} fbeValue EndReadSnapshotResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelEndReadSnapshotResult}
   * @param {EndReadSnapshotResult} fbeValue EndReadSnapshotResult value
   */
  setFields (fbeValue) {
  }
}

export { FieldModelEndReadSnapshotResult };

/**
 * Fast Binary Encoding EndReadSnapshotResult model
 */
class EndReadSnapshotResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelEndReadSnapshotResult(this.buffer, 4)
  }

  /**
   * Get the EndReadSnapshotResult model
   * @this {!EndReadSnapshotResultModel}
   * @returns {!FieldModelEndReadSnapshotResult} model EndReadSnapshotResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!EndReadSnapshotResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!EndReadSnapshotResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return EndReadSnapshotResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!EndReadSnapshotResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelEndReadSnapshotResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!EndReadSnapshotResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!EndReadSnapshotResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!EndReadSnapshotResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!EndReadSnapshotResultModel}
   * @param {!EndReadSnapshotResult} value EndReadSnapshotResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!EndReadSnapshotResultModel}
   * @param {!EndReadSnapshotResult} value EndReadSnapshotResult value, defaults is new EndReadSnapshotResult()
   * @return {!object} Deserialized EndReadSnapshotResult value and its size
   */
  deserialize (value = new EndReadSnapshotResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new EndReadSnapshotResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new EndReadSnapshotResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!EndReadSnapshotResultModel}
   * @param {!number} prev Previous EndReadSnapshotResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { EndReadSnapshotResultModel };
